import chunk from 'lodash/chunk';
import pLimit from 'p-limit';

import { AtlassianClientFetchError } from '@canvas/atlassian-client/atlassian/error';
import { type RequestInitWithMetadata } from '@canvas/atlassian-client/atlassian/types';
import { GraphQLNetworkError } from '@canvas/atlassian-client/graphql';
import { isError } from '@canvas/atlassian-client/is-error';
import { JiraClient } from '@canvas/atlassian-client/jira';
import {
  type JiraPaginationInfo,
  type JiraIssueListQueryNode,
  type JiraIssueListQueryResponse,
  type JiraCreateIssueLinkOpts,
  type JiraDeleteIssueLinkByIdOpts,
  type JiraDeleteIssueLinkOpts,
  type JiraEditIssueFields,
  type JiraGetIssueLinksOpts,
  type JiraGraphQLParentCandidatesWithInfoForNewIssueVariables,
  type JiraProjectFieldBean,
  type JiraRestSingleProjectResponse,
  type JiraRestProjectVersion,
  type JiraIssuesByKeyWithLinksQueryResponse,
  type JiraIssuesByKeyWithChildrenQueryResponse,
  type JiraGraphQLIssueFieldResponse,
} from '@canvas/atlassian-client/jira/types';
import { aPersonCanUseJiraIntegrations } from '@canvas/capability-summaries';
import { getFrameApi } from '@canvas/frame-api/host-api/host-frame-context';
import { FailureReasons, hostNotifier } from '@canvas/host-notifier';
import { retryWithBackoff } from '@canvas/retry';
import { CustomerImpact, captureExceptionForCapability } from '@canvas/sentry-react';

import {
  type IssueField,
  type IssueFieldOption,
  JiraImportErrorCodes,
  type JiraImportError,
  JIRA_IMPORT_ERROR_SYMBOL,
} from './jira-service-types';
import { type CloudId, type Site } from './site-types';
import { toIssueField, toProject } from './transformers/jira-transformers';

/**
 * Creates a JiraImportError with an optional cause.
 *
 * When a cause is provided and it's an Error instance, it will be preserved
 * using the standard Error.cause property. This ensures that stack traces and
 * other error details are available when the error is reported to Sentry.
 *
 * @param message - The error message
 * @param code - The JiraImportError code
 * @param cause - Optional original error to preserve as cause
 * @returns A JiraImportError with the cause preserved if provided
 */
export const createJiraImportError = (
  message: string,
  code: JiraImportErrorCodes,
  cause?: unknown
): JiraImportError => {
  const error = new Error(
    message,
    cause instanceof Error ? { cause } : undefined
  ) as JiraImportError;
  error.code = code;
  error[JIRA_IMPORT_ERROR_SYMBOL] = true;
  return error;
};

interface LoadIssueFieldsOpts {
  projectId: string;
  issueTypeId?: string;
  requestTypeKey?: string;
}

interface CreateIssueOpts {
  fields: { [key: string]: string | number };
  update: { [key: string]: any };
}

interface GetFieldsForProjectsOpts {
  projectId: number[];
  workTypeId: string[];
}

const FAVICON_URL =
  'https://canvas-frontend.prod-east.frontend.public.atl-paas.net/assets/whiteboard-logo.svg';

// Safety cap for paginated fetch loops when aggregating many issues
const MAX_PAGES = 100;
const PAGE_SIZE = 50; // IssueSearchStableQuery seems to return up to 50 items per page at most
const GET_FIELDS_FOR_PROJECTS_PAGE_SIZE = 100;
const MAX_GET_FIELDS_FOR_PROJECTS_PAGES = 10;
const MAX_GET_FIELDS_FOR_PROJECTS_TIMEOUT = 15000;

const fetcher = async (input: RequestInfo, init?: RequestInitWithMetadata) =>
  await retryWithBackoff({
    callback: async () => {
      const result = await globalThis.fetch(input, init);

      // Trigger a retry for server errors and rate limiting
      if (result.status >= 500 || result.status === 429) {
        const url = input instanceof Request ? input.url : input;
        // If we fail in a pollinator test, append this failure to our annotations
        // so it gets picked up in prelude and pushed to signalFX as such
        hostNotifier.reportExternalFailure({
          source: FailureReasons.PRODUCT_API_FAILURE,
          location: 'jira-service',
          endpoint: url,
          dependencyId: 'jira',
        });
        throw new AtlassianClientFetchError(
          result.status,
          result.statusText,
          await result.text(),
          undefined,
          init?.metadata
        );
      }

      return result;
    },
    retryTimeMs: 2500,
    maxRetries: 5,
  });

export class JiraService {
  private fields: IssueField[] = [];
  private mergedFieldOptions = new Map<string, IssueFieldOption[]>();

  public constructor(
    private client = new JiraClient(fetcher),
    private readonly origin: string = globalThis.location.origin
  ) {}

  /**
   * Convert a natural language prompt into JQL using Jira GraphQL.
   */
  public async naturalLanguageToJql(
    site: CloudId,
    prompt: string
  ): Promise<{ jql: string }> {
    const cloudId = site.cloudId;
    const trimmed = prompt?.trim() ?? '';
    if (!cloudId) {
      throw createJiraImportError(
        'Cloud ID is required',
        JiraImportErrorCodes.JIRA_SEARCH_FAILED
      );
    }
    if (!trimmed) {
      throw createJiraImportError(
        'No JQL generated',
        JiraImportErrorCodes.JIRA_NLP_SEARCH_FAILED_TO_GENERATE_JQL
      );
    }

    const response = await this.client.naturalLanguageToJql(this.origin, {
      cloudId,
      naturalLanguageInput: trimmed,
    });

    // Top-level GraphQL errors
    // Check if the response contains GraphQL errors
    if (response.errors && response.errors.length > 0) {
      throw createJiraImportError(
        'GraphQL error',
        JiraImportErrorCodes.JIRA_NLP_SEARCH_FAILED_TO_GENERATE_JQL
      );
    }

    const payload = response?.data?.jira?.naturalLanguageToJql;

    if (!payload) {
      throw createJiraImportError(
        'Request failed',
        JiraImportErrorCodes.JIRA_SEARCH_FAILED
      );
    }

    if (payload.generatedJQLError) {
      throw createJiraImportError(
        'NLP failed to generate JQL',
        JiraImportErrorCodes.JIRA_NLP_SEARCH_FAILED_TO_GENERATE_JQL
      );
    }

    const jql = payload.generatedJQL;
    if (typeof jql === 'string' && jql.trim().length > 0) {
      return { jql };
    }

    throw createJiraImportError(
      'No JQL generated',
      JiraImportErrorCodes.JIRA_NLP_SEARCH_FAILED_TO_GENERATE_JQL
    );
  }

  public async getProjects(
    site: Site,
    query?: string,
    shouldThrow?: boolean,
    paginationCursor?: number
  ) {
    const baseUrl = this.getTenantBaseUrl(site);

    let response;

    try {
      response = await this.client.getProjects(baseUrl, query, {
        startAt: paginationCursor,
      });
    } catch (err) {
      console.error(err);
      if (shouldThrow) {
        throw err;
      }
      captureExceptionForCapability(
        err,
        {
          tags: {
            captureLocation: 'getProjects-no-throw',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
        aPersonCanUseJiraIntegrations
      );
    }
    const requestStartedAt = response?.startAt ?? 0;
    const requestMaxResults = response?.maxResults ?? 0;

    const positionOfLastItem = requestStartedAt + requestMaxResults;
    return {
      all:
        response?.values?.map((project) =>
          toProject(project, baseUrl.origin, site.cloudId)
        ) ?? [],
      cursor: positionOfLastItem ? positionOfLastItem + 1 : 0,
      isLast: response?.isLast ?? false,
    };
  }

  public async getProjectByKeyOrId(
    site: Site,
    projectKeyOrId: string,
    shouldThrow?: boolean
  ) {
    const baseUrl = this.getTenantBaseUrl(site);

    let response: JiraRestSingleProjectResponse;

    try {
      response = await this.client.getProject(baseUrl, projectKeyOrId);

      if (response && 'id' in response) {
        return toProject(response, baseUrl.origin, site.cloudId);
      }
    } catch (err) {
      console.error(err);
      if (shouldThrow) {
        throw err;
      }
      captureExceptionForCapability(
        err,
        {
          tags: {
            captureLocation: 'getProjectByKeyOrId-no-throw',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
        aPersonCanUseJiraIntegrations
      );
    }

    return null;
  }

  public async getProjectVersions(
    site: CloudId,
    projectKeyOrId: string,
    shouldThrow?: boolean
  ) {
    const baseUrl = this.getTenantBaseUrl(site);

    let response: JiraRestProjectVersion[];

    try {
      response = await this.client.getProjectVersions(baseUrl, projectKeyOrId);

      if (Array.isArray(response)) {
        return response;
      }
    } catch (err) {
      console.error(err);
      if (shouldThrow) {
        throw err;
      }
      captureExceptionForCapability(
        err,
        {
          tags: {
            captureLocation: 'getProjectVersions-no-throw',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
        aPersonCanUseJiraIntegrations
      );
    }

    return null;
  }

  public async loadIssueFields(
    site: CloudId,
    { projectId, issueTypeId, requestTypeKey }: LoadIssueFieldsOpts
  ) {
    const baseUrl = this.getTenantBaseUrl(site);

    const response = await this.client.getIssueCreateFields(baseUrl, {
      project: { projectId },
      issueType: issueTypeId ? { issueTypeId } : null,
      defaultsValues: null,
      requestType: requestTypeKey ? { key: requestTypeKey } : null,
    });

    const createFields = response.data?.jiraIssueCreateFields as
      | {
          fields: { nodes: JiraGraphQLIssueFieldResponse[] };
          errors?: Array<{
            identifier?: string;
            extensions?: Array<{ message?: string; errorType?: string }>;
          }>;
        }
      | undefined;

    if (createFields?.errors?.length) {
      const messages = createFields.errors.flatMap((e) =>
        (e.extensions ?? []).map((x) => (x.message ?? '').toLowerCase())
      );
      const isNoAccess = messages.some(
        (m) =>
          m.includes('not authorised') ||
          m.includes('not authorized') ||
          m.includes('cannot create') ||
          m.includes('not authorized to perform')
      );
      if (isNoAccess) {
        const firstMessage = createFields.errors
          .flatMap((e) => e.extensions ?? [])
          .map((x) => x.message)
          .find(Boolean);
        throw new GraphQLNetworkError(
          firstMessage ?? 'No access to create issues in this project',
          { statusCode: 403 }
        );
      }
    }

    this.fields = response.data.jiraIssueCreateFields.fields.nodes.map(
      (f: JiraGraphQLIssueFieldResponse) => toIssueField(baseUrl.href, f)
    );

    return this.fields;
  }

  public getOptionsForField(fieldId: string): IssueFieldOption[] {
    if (this.mergedFieldOptions.has(fieldId)) {
      return this.mergedFieldOptions.get(fieldId) ?? [];
    }
    return this.fields.find((f) => f.id === fieldId)?.options ?? [];
  }

  public setMergedFieldOptions(
    fieldOptionsPerIssueType: Map<string, Map<string, IssueFieldOption[]>>
  ): void {
    this.mergedFieldOptions.clear();

    const allFieldIds = new Set<string>();
    for (const issueTypeOptions of fieldOptionsPerIssueType.values()) {
      for (const fieldId of issueTypeOptions.keys()) {
        allFieldIds.add(fieldId);
      }
    }

    for (const fieldId of allFieldIds) {
      const optionsMap = new Map<string, IssueFieldOption>();
      for (const issueTypeOptions of fieldOptionsPerIssueType.values()) {
        const options = issueTypeOptions.get(fieldId);
        if (options) {
          for (const option of options) {
            const key = String(option.value);
            if (!optionsMap.has(key)) {
              optionsMap.set(key, option);
            }
          }
        }
      }
      if (optionsMap.size > 0) {
        this.mergedFieldOptions.set(fieldId, Array.from(optionsMap.values()));
      }
    }
  }

  public clearMergedFieldOptions(): void {
    this.mergedFieldOptions.clear();
  }

  /**
   * This method is used to get the work type and fields for a given site and project.
   * @param site - The site to get the fields for.
   * @param opts - The options for the request.
   * @returns The fields for the project.
   */
  public async getFieldsForProjects(
    site: Site,
    opts: GetFieldsForProjectsOpts
  ): Promise<JiraProjectFieldBean[]> {
    const baseUrl = this.getTenantBaseUrl(site);
    const { projectId, workTypeId } = opts;
    const request = { projectId, workTypeId };

    const fetchPromise = (async (): Promise<JiraProjectFieldBean[]> => {
      let requestCount = 0;
      let response = await this.client.getFieldsForProjects(baseUrl, request, {
        maxResults: GET_FIELDS_FOR_PROJECTS_PAGE_SIZE,
      });
      const values: JiraProjectFieldBean[] = [...response.values];

      while (
        !response.isLast &&
        response.nextPage &&
        requestCount < MAX_GET_FIELDS_FOR_PROJECTS_PAGES
      ) {
        const nextPageUrl = new URL(response.nextPage);
        const startAt = nextPageUrl.searchParams.get('startAt');
        const maxResults = nextPageUrl.searchParams.get('maxResults');
        if (startAt === null || maxResults === null) {
          break;
        }
        requestCount += 1;
        response = await this.client.getFieldsForProjects(baseUrl, request, {
          startAt: Number(startAt),
          maxResults: Number(maxResults),
        });
        if (response.values == null || response.values.length === 0) {
          break;
        }
        values.push(...response.values);
      }

      return values;
    })();

    // Timeout the request if it takes too long
    const timeoutPromise = new Promise<never>((_, reject) => {
      const timeoutId = setTimeout(
        () => reject(new Error('getFieldsForProjects timed out')),
        MAX_GET_FIELDS_FOR_PROJECTS_TIMEOUT
      );
      void fetchPromise.finally(() => {
        clearTimeout(timeoutId);
      });
    });

    return await Promise.race([fetchPromise, timeoutPromise]);
  }

  public async createIssue(site: Site, opts: CreateIssueOpts) {
    const baseUrl = this.getTenantBaseUrl(site);

    const {
      fields: { site: siteField, project, issuetype: issueType, ...rest },
      update,
    } = opts;
    void siteField;

    const issue = await this.client.createIssue(
      baseUrl,
      {
        project: { id: project as string },
        issuetype: { id: issueType as string },
        ...rest,
      },
      update
    );

    if (isError(issue)) {
      // TODO CNS-2523
      return issue;
    }

    const frameApi = getFrameApi();

    // Creates a web link without blocking execution.
    this.client
      .createRemoteLink(baseUrl, {
        issueKey: issue.key,
        title: frameApi.title,
        url: new URL(frameApi.href),
        icon: FAVICON_URL,
      })
      .catch(console.error);

    return issue;
  }

  public async editIssue(
    site: string,
    issueKey: string,
    fields: JiraEditIssueFields,
    update: Record<string, any> = {}
  ) {
    const baseUrl = this.getTenantBaseUrl({ cloudId: site });

    return this.client.editIssue(baseUrl, issueKey, fields, update);
  }

  public async createIssueLink(site: Site, opts: JiraCreateIssueLinkOpts) {
    const baseUrl = this.getTenantBaseUrl(site);

    // create issue link does not return the link that was created
    await this.client.createIssueLink(baseUrl, {
      type: opts.type,
      inwardIssue: opts.inwardIssue,
      outwardIssue: opts.outwardIssue,
    });

    // but we can query for it afterward by type
    // as advised here https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issue-links/#api-rest-api-3-issuelink-post
    // as it's not possible to have 2 issue links between the same 2 issues by type
    const issueLinks = await this.client.getIssueLinks(baseUrl, {
      sourceIssueKey: opts.inwardIssue,
      targetIssueKey: opts.outwardIssue,
    });

    const issueLink = issueLinks.find((issueLink) => issueLink.type.id === opts.type);
    if (!issueLink) {
      throw new Error('Created issue link could not be found');
    }
    return issueLink;
  }

  public async getIssueLinks(site: Site, opts: JiraGetIssueLinksOpts) {
    const baseUrl = this.getTenantBaseUrl(site);

    const issueLinks = await this.client.getIssueLinks(baseUrl, {
      sourceIssueKey: opts.sourceIssueKey,
      targetIssueKey: opts.targetIssueKey,
    });

    return issueLinks;
  }

  public async getIssueLinkTypes(site: Site) {
    const baseUrl = this.getTenantBaseUrl(site);

    return this.client.getIssueLinkTypes(baseUrl);
  }

  /**
   * Gets linked issues for many issue keys by batching keys into OR-ed JQL clauses (default 5 at a time) to reduce total roundtrips.
   */
  public async getLinkedIssues(
    site: Site,
    opts: { issueKeys: string[]; linkType?: string; batchSize?: number }
  ): Promise<JiraIssueListQueryResponse> {
    return this.queryByBatchedClauses(site, opts.issueKeys, opts.batchSize, (k) =>
      opts.linkType
        ? `issue in linkedIssues("${k}", "${opts.linkType}")`
        : `issue in linkedIssues("${k}")`
    );
  }

  /**
   * Gets child issues for many parent keys by batching keys into OR-ed JQL clauses (default 5 at a time) to reduce total roundtrips.
   */
  public async getChildIssues(
    site: Site,
    opts: { issueKeys: string[]; batchSize?: number }
  ) {
    return this.queryByBatchedClauses(
      site,
      opts.issueKeys,
      opts.batchSize,
      (k) => `parent = "${k}"`
    );
  }

  private async queryByBatchedClauses(
    site: Site,
    rootKeys: string[],
    batchSizeRaw: number | undefined,
    renderClause: (key: string) => string
  ): Promise<JiraIssueListQueryResponse> {
    const keys = (rootKeys ?? []).filter(Boolean);
    if (keys.length === 0) {
      return this.toIssueListResponse('', []);
    }

    const batchSize = Math.max(1, Math.min(batchSizeRaw ?? 5, 50));
    const allNodes: JiraIssueListQueryNode[] = [];

    for (let i = 0; i < keys.length; i += batchSize) {
      const clause = keys
        .slice(i, i + batchSize)
        .map(renderClause)
        .join(' OR ');
      const jql = `(${clause})`;
      const nodes = await this.fetchAllIssuesByJql(
        { cloudId: site.cloudId },
        jql,
        PAGE_SIZE,
        MAX_PAGES
      );
      for (const n of nodes) {
        allNodes.push(n);
      }
    }

    return this.toIssueListResponse('batched', allNodes);
  }

  private async fetchAllIssuesByJql(
    site: CloudId,
    jql: string,
    pageSize: number,
    maxPages: number
  ): Promise<JiraIssueListQueryNode[]> {
    let page = 0;
    let paginationInfo: JiraPaginationInfo | undefined = undefined;
    const allNodes: JiraIssueListQueryNode[] = [];

    for (let i = 0; i < maxPages; i++) {
      const res = await this.queryIssues(site, jql, { page, pageSize, paginationInfo });
      if (isError(res)) {
        break;
      }

      const batch = res.data?.issues?.nodes ?? [];
      for (const n of batch) {
        allNodes.push(n);
      }

      const info = res.data?.issues?.pageInfo;
      if (!info?.hasNextPage) {
        break;
      }
      paginationInfo = info;
      page = (info.page ?? page) + 1;
    }

    return allNodes;
  }

  public async fetchAllIssueIdsByJql(
    site: CloudId,
    jql: string,
    maxItemsLimit: number
  ): Promise<string[]> {
    const numPages = Math.ceil(maxItemsLimit / PAGE_SIZE);
    const allNodes = await this.fetchAllIssuesByJql(site, jql, PAGE_SIZE, numPages);
    const issueIdsSet = new Set<string>();

    for (const node of allNodes) {
      issueIdsSet.add(node.issueId.toString());
    }

    return Array.from(issueIdsSet);
  }

  public async getIssuesByKeyWithLinks(
    site: Site,
    opts: { issueKeys: string[]; linkType?: string; batchSize?: number }
  ): Promise<JiraIssuesByKeyWithLinksQueryResponse> {
    const baseUrl = this.getTenantBaseUrl(site);
    const keys = opts.issueKeys.filter(Boolean);

    // If no keys or within limit, make single request
    const chunkSize = 95; // Use 95 to stay well under the 100 key limit
    if (keys.length === 0) {
      return {
        data: {
          jira: {
            issuesByKey: [],
          },
        },
      };
    }

    if (keys.length <= chunkSize) {
      return this.client.queryIssuesByKeyWithLinks(baseUrl, {
        keys,
        cloudId: site.cloudId,
      });
    }

    // Chunk keys and make multiple requests in parallel with controlled concurrency
    // Use p-limit to avoid overwhelming the API with too many concurrent requests
    const limit = pLimit(3); // Process max 3 chunks in parallel to avoid rate limiting
    const allIssues: JiraIssuesByKeyWithLinksQueryResponse['data']['jira']['issuesByKey'] =
      [];

    const chunks = chunk(keys, chunkSize);

    const chunkPromises = chunks.map((chunkKeys, index) =>
      limit(async () => {
        try {
          const chunkResponse = await this.client.queryIssuesByKeyWithLinks(baseUrl, {
            keys: chunkKeys,
            cloudId: site.cloudId,
          });

          return chunkResponse.data?.jira?.issuesByKey || [];
        } catch (error) {
          // Log error but continue with other chunks to avoid failing entire operation
          console.error(`Failed to fetch issues for chunk ${index}:`, error);
          captureExceptionForCapability(
            error,
            {
              tags: {
                captureLocation: 'getIssuesByKeyWithLinks-chunk-error',
                customerImpact: CustomerImpact.CUSTOMERS,
                chunkIndex: index.toString(),
                chunkSize: chunkKeys.length.toString(),
              },
            },
            aPersonCanUseJiraIntegrations
          );
          return []; // Return empty array for failed chunks
        }
      })
    );

    // Wait for all chunks to complete and flatten results
    const chunkResults = await Promise.allSettled(chunkPromises);

    chunkResults.forEach((result) => {
      if (result.status === 'fulfilled') {
        for (const issue of result.value) {
          allIssues.push(issue);
        }
      }
    });

    return {
      data: {
        jira: {
          issuesByKey: allIssues,
        },
      },
    };
  }

  /**
   * Get child issues for a given list of parent issue keys using GraphQL
   * Handles chunking for large key sets due to API limits (100 keys max)
   */
  public async getIssuesByKeyWithChildren(
    site: Site,
    opts: { issueKeys: string[] }
  ): Promise<JiraIssuesByKeyWithChildrenQueryResponse> {
    const baseUrl = this.getTenantBaseUrl(site);
    const { issueKeys: keys } = opts;

    // If no keys or within limit, make single request
    const chunkSize = 95; // Use 95 to stay well under the 100 key limit
    if (keys.length === 0) {
      return {
        data: {
          jira: {
            issuesByKey: [],
          },
        },
      };
    }

    if (keys.length <= chunkSize) {
      return this.client.queryIssuesByKeyWithChildren(baseUrl, {
        keys,
        cloudId: site.cloudId,
      });
    }

    // Chunk keys and make multiple requests in parallel with controlled concurrency
    // Use p-limit to avoid overwhelming the API with too many concurrent requests
    const limit = pLimit(3); // Process max 3 chunks in parallel to avoid rate limiting
    const allIssues: JiraIssuesByKeyWithChildrenQueryResponse['data']['jira']['issuesByKey'] =
      [];

    const chunks = chunk(keys, chunkSize);

    const chunkPromises = chunks.map((chunkKeys, index) =>
      limit(async () => {
        try {
          const chunkResponse = await this.client.queryIssuesByKeyWithChildren(baseUrl, {
            keys: chunkKeys,
            cloudId: site.cloudId,
          });

          return chunkResponse.data?.jira?.issuesByKey || [];
        } catch (error) {
          // Log error but continue with other chunks to avoid failing entire operation
          console.error(
            `Failed to fetch issues with children for chunk ${index}:`,
            error
          );
          captureExceptionForCapability(
            error,
            {
              tags: {
                captureLocation: 'getIssuesByKeyWithChildren-chunk-error',
                customerImpact: CustomerImpact.CUSTOMERS,
                chunkIndex: index.toString(),
                chunkSize: chunkKeys.length.toString(),
              },
            },
            aPersonCanUseJiraIntegrations
          );
          return []; // Return empty array for failed chunks
        }
      })
    );

    // Wait for all chunks to complete and flatten results
    const chunkResults = await Promise.allSettled(chunkPromises);

    chunkResults.forEach((result) => {
      if (result.status === 'fulfilled') {
        for (const issue of result.value) {
          allIssues.push(issue);
        }
      }
    });

    return {
      data: {
        jira: {
          issuesByKey: allIssues,
        },
      },
    };
  }

  /**
   * Converts a list of issues to a JiraIssueListQueryResponse
   */
  private toIssueListResponse(
    jql: string,
    nodes: JiraIssueListQueryNode[]
  ): JiraIssueListQueryResponse {
    return {
      data: {
        issues: {
          nodes,
          totalCount: nodes.length,
          pageSize: nodes.length,
          jql,
          pageInfo: { page: 0, hasNextPage: false, hasPreviousPage: false },
        },
      },
    };
  }

  public async deleteIssueLinkById(site: Site, opts: JiraDeleteIssueLinkByIdOpts) {
    const baseUrl = this.getTenantBaseUrl(site);

    await this.client.deleteIssueLinkById(baseUrl, {
      linkId: opts.linkId,
    });
  }

  public async deleteIssueLink(site: Site, opts: JiraDeleteIssueLinkOpts) {
    const baseUrl = this.getTenantBaseUrl(site);

    await this.client.deleteIssueLink(baseUrl, {
      linkTypeId: opts.linkTypeId,
      sourceIssueKey: opts.sourceIssueKey,
      targetIssueKey: opts.targetIssueKey,
    });
  }

  public getTenantBaseUrl(site: CloudId) {
    const sitePath = `/gateway/api/ex/jira/${site.cloudId}`;
    return new URL(sitePath, this.origin);
  }

  public getOptionForField(
    fieldId: string,
    value: string | number
  ): IssueFieldOption | undefined {
    const options = this.getOptionsForField(fieldId);
    return options.find((o) => o.value === value);
  }

  public async queryIssues(
    site: CloudId,
    query: string,
    context: {
      page: number;
      pageSize: number;
      signal?: AbortSignal;
      paginationInfo?: JiraPaginationInfo;
    }
  ) {
    const baseUrl = this.getTenantBaseUrl(site);
    return await this.client.queryIssues(baseUrl, site.cloudId, query, context);
  }

  public async queryFirst1000IssueIds(site: CloudId, query: string) {
    const baseUrl = this.getTenantBaseUrl(site);
    return await this.client.queryFirst1000IssueIds(baseUrl, query);
  }
  public async queryIssuesByIds(site: CloudId, ids: number[]) {
    const baseUrl = this.getTenantBaseUrl(site);
    return await this.client.queryIssuesByIds(baseUrl, ids);
  }

  /**
   * Delegates to the JiraClient to resolve an issue field's values
   */
  public async resolveSearchField<T>(
    site: CloudId,
    {
      field,
      query,
      projectId,
      maxResults,
      startAt,
    }: {
      field: IssueField;
      query: string;
      maxResults?: number;
      projectId?: string | number;
      startAt?: number;
    }
  ) {
    const baseUrl = this.getTenantBaseUrl(site);

    if (!field.searchUrl) {
      return null;
    }

    const searchUrl = new URL(field.searchUrl);

    searchUrl.host = baseUrl.host;
    searchUrl.pathname = `${baseUrl.pathname}/rest${
      searchUrl.pathname.split('/rest')[1]
    }`;

    return await this.client.resolveSearchField<T>(searchUrl.href, {
      query,
      projectId,
      maxResults,
      startAt,
    });
  }

  /**
   * Delegates to the JiraClient to resolve parent issues (like epics) for a new issue
   */
  public async resolveParentCandidatesForNewIssue(
    site: CloudId,
    opts: JiraGraphQLParentCandidatesWithInfoForNewIssueVariables
  ) {
    const baseUrl = this.getTenantBaseUrl(site);

    return await this.client.resolveParentCandidatesForNewIssue(baseUrl, {
      cloudId: opts.cloudId,
      projectId: opts.projectId,
      issueTypeId: opts.issueTypeId,
      searchTerm: opts.searchTerm,
    });
  }
}
